import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { tmsApi, type OrderTemplate, type OrderTemplatePreview } from '../client';

export const orderTemplateKeys = {
  all: ['tms', 'orderTemplate'] as const,
  byCustomer: (organisationId: string, customerPartyId: string, includeArchived: boolean) =>
    ['tms', 'orderTemplate', 'customer', organisationId, customerPartyId, includeArchived] as const,
  preview: (id: string) => ['tms', 'orderTemplate', 'preview', id] as const,
};

/** A customer's order templates (New order → "Start from a template"), most used first. */
export function useOrderTemplates(
  organisationId: string | null,
  customerPartyId: string | null,
  includeArchived = false,
) {
  return useQuery<OrderTemplate[], Error>({
    queryKey:
      organisationId && customerPartyId
        ? orderTemplateKeys.byCustomer(organisationId, customerPartyId, includeArchived)
        : orderTemplateKeys.all,
    queryFn: () => tmsApi.getOrderTemplates(organisationId!, customerPartyId!, includeArchived),
    enabled: !!organisationId && !!customerPartyId,
    staleTime: 30 * 1000,
  });
}

/** A template as an order + tasks, for the copy panel. */
export function useOrderTemplatePreview(id: string | null) {
  return useQuery<OrderTemplatePreview, Error>({
    queryKey: id ? orderTemplateKeys.preview(id) : orderTemplateKeys.all,
    queryFn: () => tmsApi.getOrderTemplatePreview(id!),
    enabled: !!id,
    staleTime: 30 * 1000,
  });
}

/** Every template write refreshes the lists (and previews) — they're small. */
function useTemplateMutation<TVars>(fn: (vars: TVars) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, TVars>({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orderTemplateKeys.all });
    },
  });
}

export function useSaveOrderTemplate() {
  const qc = useQueryClient();
  return useMutation<
    OrderTemplate,
    Error,
    { sourceOrderId: string; name: string; description: string | null }
  >({
    mutationFn: (body) => tmsApi.saveOrderTemplate(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orderTemplateKeys.all });
    },
  });
}

export function useUpdateOrderTemplate() {
  return useTemplateMutation((v: { id: string; name: string; description: string | null }) =>
    tmsApi.updateOrderTemplate(v.id, { name: v.name, description: v.description }),
  );
}

export function useArchiveOrderTemplate() {
  return useTemplateMutation((v: { id: string; archive: boolean }) =>
    v.archive ? tmsApi.archiveOrderTemplate(v.id) : tmsApi.restoreOrderTemplate(v.id),
  );
}

export function useDeleteOrderTemplate() {
  return useTemplateMutation((v: { id: string }) => tmsApi.deleteOrderTemplate(v.id));
}
